import { ComponentType, lazy } from "react";
import {
  ManagementRole,
  MANAGEMENT_ROLES,
  normalizeManagementRole,
  useAuthProfile,
} from "../contexts/authProfile";

const ROLES_PRIO: Record<ManagementRole, number> = {
  superadmin: 3,
  mgmt_admin: 2,
  project_manager: 1,
  service_user: 0,
};

const Page403 = lazy(() => import("../modules/403/403"));

const isManagementRole = (role: string): role is ManagementRole =>
  MANAGEMENT_ROLES.includes(role as ManagementRole);

const getRolePriority = (role: string): number => {
  const normalized = normalizeManagementRole(role);

  return normalized ? ROLES_PRIO[normalized] : -1;
};

const hasRequiredRole = (
  managementRoles: string[] | undefined,
  requiredRole: ManagementRole
): boolean => {
  const userRole = managementRoles?.[0];

  if (!userRole || !isManagementRole(userRole)) {
    return false;
  }

  return (
    requiredRole === userRole ||
    ROLES_PRIO[userRole] >= ROLES_PRIO[requiredRole]
  );
};

const withRole = (role: ManagementRole) => {
  function withRoleComponent<Props extends object>(
    Component: ComponentType<Props>
  ) {
    return function RoleProtectedComponent(props: Props) {
      const user = useAuthProfile();

      if (hasRequiredRole(user?.management_roles, role)) {
        return <Component {...props} />;
      }
      return <Page403 />;
    };
  }

  return withRoleComponent;
};

const checkRole = (role: ManagementRole): boolean => {
  const user = useAuthProfile();

  return hasRequiredRole(user?.management_roles, role);
};

const checkExactRole = (role: ManagementRole): boolean => {
  const user = useAuthProfile();

  if ((user?.management_roles || []).length <= 0) {
    return false;
  }

  const userRole = user?.management_roles[0] || -1;

  return role === userRole;
};

const useCanAssignRole = (): ((role: string) => boolean) => {
  const user = useAuthProfile();
  const userRole = user?.management_roles?.[0];

  return (role: string) => {
    if (!userRole || !isManagementRole(userRole)) {
      return false;
    }

    const rolePriority = getRolePriority(role);

    if (rolePriority < 0) {
      return true;
    }

    return ROLES_PRIO[userRole] >= rolePriority;
  };
};

export { withRole, checkRole, checkExactRole, useCanAssignRole };
